/**
 * useQueryTemplate Hook
 *
 * React hook for fetching and executing parameterized query templates.
 * Designed for use with chart components that need to display data from
 * reusable query templates with variable overrides.
 *
 * Features:
 * - Automatic data fetching with React Query
 * - Variable override support
 * - Caching with stale-while-revalidate
 * - Loading and error states
 * - Refetch on variable changes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import {
  queryTemplateApi,
  QueryTemplate,
  TemplateExecutionResult,
  VariableDefinition,
  ListTemplatesOptions,
} from '@/services/api/queryTemplateApi';

// ============================================================================
// Query Keys
// ============================================================================

export const queryTemplateKeys = {
  all: ['queryTemplates'] as const,
  lists: () => [...queryTemplateKeys.all, 'list'] as const,
  list: (options: ListTemplatesOptions) =>
    [...queryTemplateKeys.lists(), options] as const,
  details: () => [...queryTemplateKeys.all, 'detail'] as const,
  detail: (id: string) => [...queryTemplateKeys.details(), id] as const,
  variables: (id: string) => [...queryTemplateKeys.detail(id), 'variables'] as const,
  executions: () => [...queryTemplateKeys.all, 'execution'] as const,
  execution: (id: string, variables: Record<string, unknown>) =>
    [...queryTemplateKeys.executions(), id, variables] as const,
};

// ============================================================================
// Main Hook: useQueryTemplate
// ============================================================================

export interface UseQueryTemplateOptions {
  /** Template ID to execute */
  templateId: string | undefined;
  /** Variable overrides */
  variables?: Record<string, unknown>;
  /** Enable automatic fetching */
  enabled?: boolean;
  /** Stale time in milliseconds (default: 30 seconds) */
  staleTime?: number;
  /** Refetch interval in milliseconds (0 = disabled) */
  refetchInterval?: number;
  /** Callback when data is fetched */
  onSuccess?: (data: TemplateExecutionResult) => void;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
}

export interface UseQueryTemplateResult {
  /** Query result data */
  data: Record<string, unknown>[] | undefined;
  /** Execution metadata */
  metadata: TemplateExecutionResult['metadata'] | undefined;
  /** Is loading initial data */
  isLoading: boolean;
  /** Is fetching (includes background refetch) */
  isFetching: boolean;
  /** Error if any */
  error: Error | null;
  /** Was the result from cache */
  isCached: boolean;
  /** Refetch function */
  refetch: () => void;
  /** Update variables and refetch */
  updateVariables: (newVariables: Record<string, unknown>) => void;
}

/**
 * Hook for executing a query template with variable overrides
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, updateVariables } = useQueryTemplate({
 *   templateId: 'battery-hourly-avg',
 *   variables: { timeRange: 'last_7d', granularity: 'day' },
 * });
 *
 * // Change time range
 * updateVariables({ timeRange: 'last_30d' });
 * ```
 */
export function useQueryTemplate(
  options: UseQueryTemplateOptions
): UseQueryTemplateResult {
  const {
    templateId,
    variables = {},
    enabled = true,
    staleTime = 30000,
    refetchInterval = 0,
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  // Memoize variables to prevent unnecessary refetches
  const serializedVariables = useMemo(
    () => JSON.stringify(variables),
    [variables],
  );

  const memoizedVariables = useMemo(
    () => variables,
    [serializedVariables, variables],
  );

  // Execute template query
  const query = useQuery({
    queryKey: queryTemplateKeys.execution(templateId || '', memoizedVariables),
    queryFn: async () => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }
      return queryTemplateApi.execute(templateId, memoizedVariables, { skipCache: true });
    },
    enabled: enabled && !!templateId,
    staleTime,
    refetchInterval: refetchInterval > 0 ? refetchInterval : undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Handle success/error callbacks
  useMemo(() => {
    if (query.isSuccess && query.data && onSuccess) {
      onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, onSuccess]);

  useMemo(() => {
    if (query.isError && query.error && onError) {
      onError(query.error as Error);
    }
  }, [query.isError, query.error, onError]);

  // Update variables function
  const updateVariables = useCallback(
    (newVariables: Record<string, unknown>) => {
      if (!templateId) return;

      // Invalidate the old query and trigger new one
      queryClient.invalidateQueries({
        queryKey: queryTemplateKeys.execution(templateId, memoizedVariables),
      });

      // Prefetch with new variables
      queryClient.prefetchQuery({
        queryKey: queryTemplateKeys.execution(templateId, {
          ...memoizedVariables,
          ...newVariables,
        }),
        queryFn: () =>
          queryTemplateApi.execute(
            templateId,
            {
              ...memoizedVariables,
              ...newVariables,
            },
            { skipCache: true },
          ),
      });
    },
    [templateId, memoizedVariables, queryClient]
  );

  return {
    data: query.data?.data,
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    isCached: query.data?.metadata?.cached ?? false,
    refetch: query.refetch,
    updateVariables,
  };
}

// ============================================================================
// Additional Hooks
// ============================================================================

/**
 * Hook for fetching template metadata (without executing)
 */
export function useQueryTemplateMetadata(templateId: string | undefined) {
  return useQuery({
    queryKey: queryTemplateKeys.detail(templateId || ''),
    queryFn: () => {
      if (!templateId) throw new Error('Template ID required');
      return queryTemplateApi.getById(templateId);
    },
    enabled: !!templateId,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook for fetching template variable definitions
 */
export function useQueryTemplateVariables(templateId: string | undefined) {
  return useQuery({
    queryKey: queryTemplateKeys.variables(templateId || ''),
    queryFn: () => {
      if (!templateId) throw new Error('Template ID required');
      return queryTemplateApi.getVariables(templateId);
    },
    enabled: !!templateId,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook for listing available templates
 */
export function useQueryTemplateList(options: ListTemplatesOptions = {}) {
  return useQuery({
    queryKey: queryTemplateKeys.list(options),
    queryFn: () => queryTemplateApi.list(options),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook for validating template variables
 */
export function useValidateTemplateVariables() {
  return useMutation({
    mutationFn: ({
      templateId,
      variables,
    }: {
      templateId: string;
      variables: Record<string, unknown>;
    }) => queryTemplateApi.validate(templateId, variables),
  });
}

/**
 * Hook for creating a new template
 */
export function useCreateQueryTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: queryTemplateApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryTemplateKeys.lists() });
    },
  });
}

/**
 * Hook for updating a template
 */
export function useUpdateQueryTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      data,
    }: {
      templateId: string;
      data: Parameters<typeof queryTemplateApi.update>[1];
    }) => queryTemplateApi.update(templateId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryTemplateKeys.detail(variables.templateId),
      });
      queryClient.invalidateQueries({ queryKey: queryTemplateKeys.lists() });
    },
  });
}

/**
 * Hook for deleting a template
 */
export function useDeleteQueryTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: queryTemplateApi.delete,
    onSuccess: (_, templateId) => {
      queryClient.invalidateQueries({
        queryKey: queryTemplateKeys.detail(templateId),
      });
      queryClient.invalidateQueries({ queryKey: queryTemplateKeys.lists() });
    },
  });
}

// ============================================================================
// Utility Types
// ============================================================================

export type {
  QueryTemplate,
  TemplateExecutionResult,
  VariableDefinition,
  ListTemplatesOptions,
};

export default useQueryTemplate;
